import posthog from "posthog-js";
import PlayTriangleIcon from "../../components/PlayTriangleIcon";
import { AD_VOTE_COOLDOWN_MS, SHELL_ICON, STAR_ICON } from "./constants";


interface CandidateVoteButtonsProps {
  candidateId: number;
  votePricePearls: number;
  /** Whether an ad controller is loaded (same gate as original `adControllerRef.current`). */
  adControllerAvailable: boolean;
  adCooldowns: Record<number, number>;
  isAdCooldown: (candidateId: number) => boolean;
  onPearlVote: (candidateId: number) => void;
  onAdVote: (candidateId: number) => void;
  onStarsVote: (candidateId: number) => void;
  starsVotingCandidateId: number | null;
  votingCandidateId: number | null;
}

/** Pearl / ad / stars vote CTAs for one candidate card. */
function CandidateVoteButtons({
  candidateId,
  votePricePearls,
  adControllerAvailable,
  adCooldowns,
  isAdCooldown,
  onPearlVote,
  onAdVote,
  onStarsVote,
  starsVotingCandidateId,
  votingCandidateId,
}: CandidateVoteButtonsProps) {
  const onCd = isAdCooldown(candidateId);
  const cdEnd = adCooldowns[candidateId] || 0;
  // Cooldown UI snapshot — same Date.now() read as the pre-extract VoteListScreen.
  // eslint-disable-next-line react-hooks/purity -- intentional per-render cooldown progress
  const now = Date.now();
  const cdRemaining = Math.max(0, cdEnd - now);
  const cdProgress = onCd ? ((AD_VOTE_COOLDOWN_MS - cdRemaining) / AD_VOTE_COOLDOWN_MS) * 100 : 0;
  const cdMinutes = Math.ceil(cdRemaining / 60000);

  return (
    <div className="flex flex-col gap-sm">
      {/* Row 1: Pearl vote + Ad vote */}
      <div className="flex gap-md">
      {/* Pearl vote CTA — golden button */}
      <button
        onClick={() => onPearlVote(candidateId)}
        className="flex flex-1 cursor-pointer items-center justify-center gap-sm rounded-pill border-0 border-none bg-[image:var(--gradient-gold-hot)] px-lg py-md shadow-[0_2px_8px_var(--color-reward-warm-alpha-40)] [transition:transform_0.1s]"
        onPointerDown={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(0.95)"; }}
        onPointerUp={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(1)"; }}
        onPointerLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(1)"; }}
      >
        <span className="[font-family:'Rubik_One',sans-serif] text-[13px] font-normal text-object-picker-surface">
          +1 Vote
        </span>
        <span className="flex items-center gap-[3px] [font-family:Lato,sans-serif] text-[13px] font-bold text-object-picker-surface">
          {votePricePearls.toLocaleString("en-US")}
          <img src={SHELL_ICON} alt="" className="h-[14px] w-[18px]" />
        </span>
      </button>

      {/* Ad vote button — per-candidate cooldown with progress bar */}
      {adControllerAvailable && (
          <button
            onClick={() => {
              posthog.capture('cityhall_ad_vote_watch_clicked', {
                candidate_id: candidateId,
              });
              onAdVote(candidateId);
            }}
            disabled={votingCandidateId === candidateId || onCd}
            className="relative flex min-w-[90px] items-center justify-center gap-xs overflow-hidden rounded-pill border-0 border-none px-[14px] py-md"
            style={{
              background: onCd
                ? "var(--color-text-dim)"
                : "linear-gradient(135deg, var(--color-success) 0%, var(--color-success-hover) 100%)",
              cursor: onCd ? "not-allowed" : "pointer",
            }}
          >
            {/* Progress fill overlay when on cooldown */}
            {onCd && (
              <div
                className="absolute top-0 bottom-0 left-0 bg-[linear-gradient(135deg,var(--color-success)_0%,var(--color-success-hover)_100%)] [transition:width_1s_linear]"
                style={{
                  width: `${cdProgress}%`,
                  borderRadius: cdProgress < 100 ? "100px 0 0 100px" : "100px",
                }}
              />
            )}
            <span className="relative z-[1] [font-family:'Rubik_One',sans-serif] text-[12px] font-normal text-white">
              {onCd ? `${cdMinutes}m` : "+5 Vote"}
            </span>
            {!onCd && (
              <span
                className="relative z-[1] inline-flex items-center justify-center"
              >
                <PlayTriangleIcon size={11} color={'var(--color-white)'} />
              </span>
            )}
          </button>
      )}
      </div>

      {/* Row 2: Stars bulk vote — full width */}
      <button
        onClick={() => onStarsVote(candidateId)}
        disabled={starsVotingCandidateId === candidateId}
        className="flex w-full items-center justify-center gap-md rounded-pill border border-solid border-onboarding-panel-alpha-50 px-xl py-md [transition:transform_0.1s,background_0.2s]"
        style={{
          background: starsVotingCandidateId === candidateId
            ? "var(--color-onboarding-panel-alpha-15)"
            : "var(--color-onboarding-panel-alpha-20)",
          cursor: starsVotingCandidateId === candidateId ? "wait" : "pointer",
          opacity: starsVotingCandidateId === candidateId ? 0.5 : 1,
        }}
        onPointerDown={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(0.97)"; }}
        onPointerUp={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(1)"; }}
        onPointerLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = "scale(1)"; }}
      >
        <span className="[font-family:'Rubik_One',sans-serif] text-[12px] font-normal text-candidate-vote-buttons-text">
          +100 Votes
        </span>
        <span className="flex items-center gap-[3px] [font-family:Lato,sans-serif] text-[12px] font-bold text-white">
          99
          <img src={STAR_ICON} alt="" className="h-xl w-xl" />
        </span>
      </button>
    </div>
  );
}

export default CandidateVoteButtons;
